import React from 'react';
import { cn } from './cn';

type ButtonVariant = 'primary' | 'secondary' | 'ghost';
type ButtonSize = 'sm' | 'md' | 'lg';

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  isLoading?: boolean;
};

export function Button({
  className,
  variant = 'primary',
  size = 'md',
  fullWidth,
  isLoading,
  disabled,
  children,
  ...props
}: ButtonProps) {
  const base =
    'inline-flex items-center justify-center gap-2 rounded-xl font-semibold transition ' +
    'active:scale-[0.99] disabled:opacity-50 disabled:pointer-events-none ' +
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/35';

  const variants: Record<ButtonVariant, string> = {
    primary: 'btn-primary text-white shadow-glow-brand',
    secondary:
      'bg-surface-overlay/60 text-text-primary border border-surface-border hover:bg-surface-overlay/85',
    ghost: 'bg-transparent text-text-secondary hover:text-text-primary hover:bg-surface-overlay/35',
  };

  const sizes: Record<ButtonSize, string> = {
    sm: 'h-9 px-3 text-sm',
    md: 'h-11 px-4 text-sm',
    lg: 'h-[52px] px-5 text-base',
  };

  return (
    <button
      {...props}
      disabled={disabled || isLoading}
      className={cn(base, variants[variant], sizes[size], fullWidth && 'w-full', className)}
    >
      {isLoading ? (
        <span className="inline-flex items-center gap-2">
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/50 border-t-transparent" />
          <span className="text-white/85">…</span>
        </span>
      ) : (
        children
      )}
    </button>
  );
}

